<template>
    <div class="billing-container">
      <!-- Left Sidebar -->
      <div class="sidebar-left">


        <!-- Status Card for Invoices Due -->
        <div
          class="card status-card"
          :class="{ 'status-unpaid': unpaidInvoicesCount > 0, 'status-paid': unpaidInvoicesCount === 0 }"
        >
          <h3>Invoices Due</h3>
          <p v-if="unpaidInvoicesCount > 0">{{ unpaidInvoicesCount }} unpaid invoices</p>
          <p v-else>No unpaid invoices at this time</p>
        </div>

        <!-- Payment Status Summary Card -->
        <div class="card payment-status-card">
          <h3>Payment Status</h3>
          <table class="status-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Paid</td>
                <td>{{ paymentStatusCount.paid }}</td>
              </tr>
              <tr>
                <td>Unpaid</td>
                <td>{{ paymentStatusCount.unpaid }}</td>
              </tr>
              <tr>
                <td>Cancelled</td>
                <td>{{ paymentStatusCount.cancelled }}</td>
              </tr>
              <tr>
                <td>Refunded</td>
                <td>{{ paymentStatusCount.refunded }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Billing Card -->
        <div class="card billing-card">
            <h3>Billing</h3>
            <ul class="billing-options">
              <li :class="{ active: activeTab === 'invoices' }" @click="setActiveTab('invoices')">My Invoices</li>
              <li :class="{ active: activeTab === 'quotes' }" @click="setActiveTab('quotes')">My Quotes</li>
              <li :class="{ active: activeTab === 'funds' }" @click="setActiveTab('funds')">Add Funds</li>
            </ul>
          </div>
      </div>

      <!-- Main Invoice Table Section -->
      <div class="main-content">
        <div class="card invoice-table-card">
          <h2>My Invoices</h2>

          <!-- Apartment Selector -->
          <div class="apartment-select">
            <label for="apartmentSelect">Select Apartment:</label>
            <select v-model="selectedApartment" @change="fetchApartmentInvoices" id="apartmentSelect">
              <option value="">All Apartments</option>
              <option v-for="apartment in apartments" :key="apartment.id" :value="apartment.id">
                {{ apartment.name }}
              </option>
            </select>
          </div>

          <!-- Search Bar -->
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search Invoices"
            class="search-bar"
          />

          <!-- Responsive Invoices Table -->
          <table v-if="pagedInvoices.length" class="invoice-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Invoice Number</th>
                <th>Room/Service</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(invoice, index) in pagedInvoices"
                :key="invoice.id"
                :class="{ 'odd-row': index % 2 === 0, 'even-row': index % 2 !== 0 }"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <router-link :to="{ name: 'InvoiceDetail', params: { id: invoice.id }}">
                    {{ invoice.invoice_number }}
                  </router-link>
                </td>
                <td>{{ invoice.rooms ? invoice.rooms.room_number : 'N/A' }}</td>
                <td>{{ invoice.total_amount }}</td>
                <td>{{ formatDueDate(invoice.due_date) }}</td>
                <td :class="paymentStatusClass(invoice.payment_status)">
                  <span class="status-box">{{ invoice.payment_status.toUpperCase() }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="no-invoices">
            <p>No invoices found.</p>
          </div>

          <!-- Pagination -->
          <div class="pagination">
            <button @click="prevPage" :disabled="currentPage === 1">Previous</button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button @click="nextPage" :disabled="currentPage === totalPages">Next</button>
          </div>
        </div>
      </div>

      <!-- Right Sidebar -->
      <div class="sidebar-right">
        <!-- Apartment Info Card -->
        <div class="card apartment-info-card">
          <h3>Apartment Info</h3>
          <p>{{ selectedApartmentDescription }}</p>
        </div>

        <!-- Additional Right Sidebar Cards -->
        <div class="card recent-activity-card">
          <h3>Recent Activity</h3>
          <p>View your recent activity here.</p>
        </div>
        <div class="card support-card">
          <h3>Support</h3>
          <p>Contact support for assistance.</p>
        </div>
      </div>
    </div>
  </template>

  <script>
  import { mapState } from 'vuex';

  export default {
    name: 'Invoices',
    data() {
      return {
        selectedApartment: '',
        searchQuery: '',
        currentPage: 1,
        pageSize: 10,
        activeTab: 'invoices'
      };
    },
    computed: {
      ...mapState({
        apartments: (state) => state.apartments,
        apartmentInvoices: (state) => state.apartmentInvoices,
      }),
      selectedApartmentDescription() {
        const apartment = this.apartments.find(
          (apt) => apt.id === this.selectedApartment
        );
        return apartment ? apartment.description : 'Select an apartment to view details.';
      },
      filteredInvoices() {
        let invoices = this.selectedApartment
          ? this.apartmentInvoices.filter(
              (invoice) => invoice.apartmentId === this.selectedApartment
            )
          : this.apartmentInvoices;

        if (this.searchQuery) {
          invoices = invoices.filter((invoice) =>
            invoice.invoice_number
              .toString()
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase())
          );
        }

        return invoices;
      },
      pagedInvoices() {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredInvoices.slice(start, start + this.pageSize);
      },
      totalPages() {
        return Math.ceil(this.filteredInvoices.length / this.pageSize);
      },
      unpaidInvoicesCount() {
        return this.filteredInvoices.filter(invoice => invoice.payment_status === 'unpaid').length;
      },
      paymentStatusCount() {
        return {
          paid: this.filteredInvoices.filter(invoice => invoice.payment_status === 'paid').length,
          unpaid: this.filteredInvoices.filter(invoice => invoice.payment_status === 'unpaid').length,
          cancelled: this.filteredInvoices.filter(invoice => invoice.payment_status === 'cancelled').length,
          refunded: this.filteredInvoices.filter(invoice => invoice.payment_status === 'refunded').length
        };
      }
    },
    methods: {
      fetchApartmentInvoices() {
        this.$store.dispatch('fetchInvoicesByApartment', this.selectedApartment);
      },
      formatDueDate(dateString) {
        const date = new Date(dateString);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
      },
      paymentStatusClass(status) {
        return {
          'paid-status': status === 'paid',
          'unpaid-status': status === 'unpaid',
          'cancelled-status': status === 'cancelled',
          'partial-status': status === 'partial',
          'refunded-status': status === 'refunded',
        };
      },
      setActiveTab(tab) {
        this.activeTab = tab;
      },
      nextPage() {
        if (this.currentPage < this.totalPages) this.currentPage++;
      },
      prevPage() {
        if (this.currentPage > 1) this.currentPage--;
      }
    }
  };
  </script>

  <style scoped>
  .billing-container {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    padding: 20px;
    width: 100%;
    background: linear-gradient(to bottom right, #0d0df7, #2f744e);
  }

  .main-content {
    flex: 2;
    min-width: 400px;
  }

  .sidebar-left,
  .sidebar-right {
    flex: 1;
    min-width: 250px;
  }

  .invoice-table-card {
    flex: 3;
  }

  .card {
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    background: linear-gradient(to bottom right, #85C1FF, #2E86C1); /* Light blue to dark blue gradient */
    margin-bottom: 20px;
  }

  .billing-card ul {
    list-style: none;
    padding: 0;
  }

  .billing-options li {
    cursor: pointer;
    padding: 8px 0;
  }

  .billing-options li.active {
    font-weight: bold;
    color: #007bff;
  }

  .status-card.status-unpaid {
    background: linear-gradient(to right, #ff7675, #ff4747);
    color: #fff;
  }

  .status-card.status-paid {
    background: linear-gradient(to right, #3ae374, #2bc16f);
    color: #fff;
  }

  .status-table th,
  .status-table td {
    padding: 5px;
    text-align: left;
  }

  .invoice-table-card h2 {
    font-size: 24px;
  }

  .invoice-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
  }

  .invoice-table th,
  .invoice-table td {
    padding: 12px;
    border-bottom: 1px solid #d8d8d8;
  }

  .invoice-table .status-box {
    padding: 5px 10px;
    border-radius: 5px;
    font-weight: bold;
    text-align: center;
  }

  .invoice-table .paid-status .status-box {
    background: #2bc16f;
    color: white;
  }

  .invoice-table .unpaid-status .status-box {
    background: #ff7675;
    color: white;
  }

  .invoice-table .cancelled-status .status-box {
    background: #f9a109;
    color: white;
  }

  .invoice-table .partial-status .status-box {
    background: #f9a109;
    color: white;
  }

  .invoice-table .refunded-status .status-box {
    background: #6a1b9a;
    color: white;
  }

  .odd-row {
    background-color: #f3f4f6;
  }

  .even-row {
    background-color: #e9ecf1;
  }

  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
  }
  </style>
